import {
  DigitResultItem,
  getDigitResultList,
} from '@/common-pages/pick-digit/digit-service';
import ResultItem from '@/common-pages/pick-digit/digit-tab/digit-result/result-item';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import React from 'react';
import {SectionList, StyleSheet, View} from 'react-native';
import TableHeder from './table-header';
import dayjs from 'dayjs';
import useInfiniteScroll from '@/common-pages/hooks/load-more.hooks';
import DigitDraw from './draw';
import {packageId} from '@/config';
import {useTranslation} from 'react-i18next';
import ShowMoreButton from '../components/show-more-button';

const defaultVisibleCount = 10;

interface DigitSection extends DigitResultItem {
  sectionKey: string;
}

export const digitTypeList = [
  {
    label: 'ALL',
    value: 0,
  },
  {
    value: 1,
    label: 'Dear Lottery',
  },
  {
    value: 2,
    label: 'Kerala Lottery',
  },
  {
    value: 3,
    label: 'Lucwin',
  },
  {
    value: 4,
    label: 'KuberLottery',
  },
  {
    value: 5,
    label: 'Chennailotto',
  },
  {
    value: 6,
    label: 'Skywin',
  },
  packageId === 1
    ? {
        value: 8,
        label: 'Bhutan',
      }
    : {
        value: 7,
        label: 'GoldenJackpot',
      },
];

const DigitResult = ({
  isActive,
  lotteryType,
  digitTypes = [],
}: {
  isActive?: boolean;
  lotteryType?: string;
  digitTypes?: {label: string; value: number}[];
}) => {
  const [loading, setLoading] = React.useState(true);
  const [list, setList] = React.useState<DigitSection[]>([]);
  const [visibleCountMap, setVisibleCountMap] = React.useState<
    Record<string, number>
  >({});
  const {onEndReachedCalledDuringMomentum} = useInfiniteScroll(
    'result-digit-normal',
  );
  const currentPage = React.useRef(1);
  const hasMores = React.useRef(true);
  const {t} = useTranslation();

  const getList = async (page = 1, loadMore = false) => {
    try {
      if (!loadMore) {
        setLoading(true);
      }
      const searchId = digitTypes?.find(item => item.label === lotteryType);
      const res = (await getDigitResultList(page, searchId?.value || 0)) || [];
      if (res.length === 0 || res[0].drawResultList.length < 10) {
        hasMores.current = false;
      }
      const target = res.map((item, index) => {
        return {
          ...item,
          data: item.drawResultList,
          sectionKey: `${page}-${item.drawDate}-${index}`,
        };
      });
      if (!loadMore) {
        setVisibleCountMap({});
      }
      setList(prev => (loadMore ? prev.concat(target) : target));
    } finally {
      setLoading(false);
      onEndReachedCalledDuringMomentum.current = true;
    }
  };

  React.useEffect(() => {
    if (isActive) {
      currentPage.current = 1;
      hasMores.current = true;
      getList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, lotteryType]);

  const onLoadMore = async () => {
    if (hasMores.current && !onEndReachedCalledDuringMomentum.current) {
      currentPage.current += 1;
      await getList(currentPage.current, true);
    }
  };

  const sections = React.useMemo(() => {
    return list.map(item => ({
      ...item,
      data: item.drawResultList.slice(
        0,
        visibleCountMap[item.sectionKey] || defaultVisibleCount,
      ),
    }));
  }, [list, visibleCountMap]);

  const handleShowMore = (section: DigitSection) => {
    setVisibleCountMap(prev => ({
      ...prev,
      [section.sectionKey]: section.drawResultList.length,
    }));
  };

  return (
    <Spin
      loading={loading}
      style={[theme.flex.flex1, theme.background.lightGrey]}>
      <SectionList
        sections={sections}
        renderSectionFooter={({section}) => {
          if (
            section.drawResultList.length <= defaultVisibleCount ||
            section.data.length >= section.drawResultList.length
          ) {
            return null;
          }

          return (
            <View style={styles.moreWrap}>
              <ShowMoreButton
                onPress={() => handleShowMore(section)}
                label={t('home.label.showMore')}
              />
            </View>
          );
        }}
        ListHeaderComponent={!loading && isActive ? <DigitDraw /> : null}
        id="result-digit-normal"
        keyExtractor={(item, index) => `${index}`}
        scrollEventThrottle={16}
        onEndReachedThreshold={0.3}
        onMomentumScrollBegin={() => {
          onEndReachedCalledDuringMomentum.current = false;
        }}
        onEndReached={onLoadMore}
        contentContainerStyle={styles.contentContainer}
        renderSectionHeader={({section: {drawDate}}) => (
          <TableHeder drawTime={dayjs(drawDate).format('DD-MM-YYYY')} />
        )}
        renderItem={({item, index}) => (
          <ResultItem
            emptyType="-"
            index={index}
            time={item.drawTime}
            result={item.drawResult}
            issueNum={item.pickName}
          />
        )}
      />
    </Spin>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    paddingBottom: 72,
  },
  moreWrap: {
    backgroundColor: '#FFFFFF',
    padding: 12,
    marginHorizontal: 12,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    flexDirection: 'row',
  },
});

export default DigitResult;
